import {
	Button,
	RadioGroup,
	Radio,
	Modal,
	ModalBody,
	ModalContent,
	useDisclosure
} from "@heroui/react";
import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import repositories from '../repositories.jsx';
import toast from 'react-hot-toast';
import {useEffect} from 'react';
import {useForm} from '@mantine/form';
import _ from 'lodash';

export default function CloneModal({data, openModal = false, ...props}) {
	const {isOpen, onOpen, onClose} = useDisclosure();
	const queryClient = useQueryClient()
	const {isOpen:projectCloneModalIsOpen,  onOpenChange:projectCloneModalOnOpenChange} = useDisclosure()

	const form = useForm({
		initialValues: {
			project_id: data?.project_id,
			translate_language: 'English',
		},
	})
	
	const updateProject = useMutation({
		mutationFn: repositories.project.cloneProject,
		onSuccess: async () => {
			await queryClient.invalidateQueries(['projects']);
			projectCloneModalOnOpenChange();
		},
		onError: (error) => {
			console.log(error)
			toast.error(error?.response?.data?.detail || 'Something went wrong')
		}
	})
	
	const languages = useQuery({
		queryKey: ['get-project-languages'],
		queryFn: () => repositories.project.languages().then((response) => {
			setTimeout(() => form.setFieldValue('translate_language', 'English'), 300)
			return response;
		})
	})
	
	useEffect(() => {
		openModal ? onOpen() : onClose()
	}, [openModal]);
	
	const inputWrapper = 'min-h-12 bg-default-500/5 border-1 capitalize data-[focus=true]:!border-primary data-[hover=true]:border-primary shadow-none';
	
	return (
		<>
			<Modal
				classNames={{
					base: 'overflow-hidden rounded-3xl border border-[#12131714] bg-white shadow-2xl',
					body: 'p-0',
					backdrop: 'bg-gradient-to-t from-zinc-900 to-zinc-900/10 backdrop-opacity-20 backdrop-blur-sm',
				}}
				size="lg"
				hideCloseButton
				isOpen={isOpen}
				onOpenChange={onClose}>
				<ModalContent>
					{(onClose) => (
						<ModalBody>
							{/* Hero band — primary */}
							<div className="relative overflow-hidden bg-gradient-to-br from-[#7659fa] via-[#8a6cff] to-[#a78bfa] px-8 pt-8 pb-9">
								<div className="pointer-events-none absolute -top-10 -right-8 h-32 w-32 rounded-full bg-white/15 blur-2xl" />
								<div className="pointer-events-none absolute -bottom-12 -left-6 h-32 w-32 rounded-full bg-white/10 blur-2xl" />
								<div className="relative flex items-center gap-4">
									<div className="grid h-14 w-14 shrink-0 place-items-center rounded-2xl bg-white/20 backdrop-blur-sm ring-1 ring-white/40">
										<i className="hgi-stroke hgi-copy-01 text-2xl text-white"></i>
									</div>
									<div>
										<h2 className="text-xl font-semibold text-white">Clone project</h2>
										<p className="mt-0.5 text-sm font-light text-white/80">Pick a language for the cloned copy.</p>
									</div>
								</div>
							</div>

							{/* Language picker */}
							<div className="px-6 pt-6">
								<label className="mb-3 block text-[11px] font-semibold uppercase tracking-wide text-slate-400">Translate language</label>
								<RadioGroup
									label=""
									orientation="horizontal"
									value={form.values.translate_language}
									onValueChange={(value) => form.setFieldValue('translate_language', value)}
									classNames={{ wrapper: 'gap-2' }}
								>
									{languages.data?.slice(1)?.map((item, itemIndex) => (
										<Radio
											size="sm"
											classNames={{
												base: 'm-0 max-w-none items-center rounded-xl border border-[#12131714] bg-[#fcfcfd] px-3 py-2 data-[selected=true]:border-[#7659fa] data-[selected=true]:bg-[#7659fa0d]',
												label: 'text-sm text-slate-700',
											}}
											isDisabled={(item?.code == 'auto' || item?.code == data?.original_language)}
											key={itemIndex}
											value={item?.code}>
											{item?.language}
										</Radio>
									))}
								</RadioGroup>
							</div>

							{/* Actions */}
							<div className="flex justify-end gap-3 px-6 py-5">
								<Button
									onPress={onClose}
									variant="bordered"
									className="h-11 rounded-xl border-1 border-[#00000014] bg-[#c8ceda4d] px-6 text-sm font-medium text-slate-700 hover:border-slate-700">
									Cancel
								</Button>
								<Button
									isLoading={updateProject.isPending}
									isDisabled={data?.original_language == form.values.translate_language}
									onPress={() => {projectCloneModalOnOpenChange(); onClose()}}
									className={`h-11 rounded-xl px-6 text-sm font-medium text-white transition ${data?.original_language == form.values.translate_language ? 'bg-[#b5a7f2] cursor-not-allowed' : 'bg-[#7659fa] shadow-lg shadow-[#7659fa40] hover:opacity-95'}`}>
									<i className="hgi-stroke hgi-copy-01 text-[15px]"></i> Clone
								</Button>
							</div>
						</ModalBody>
					)}
				</ModalContent>
			</Modal>

			{/* Cost confirmation — caution / credits */}
			<Modal
				isOpen={projectCloneModalIsOpen}
				onOpenChange={projectCloneModalOnOpenChange}
				size="lg"
				hideCloseButton
				backdrop="blur"
				classNames={{
					base: 'overflow-hidden rounded-3xl border border-[#12131714] bg-white shadow-2xl',
					body: 'p-0',
				}}>
				<ModalContent>
					{(onClose) => (
						<ModalBody>
							<div className="relative overflow-hidden bg-gradient-to-br from-[#f59e0b] via-[#f5a623] to-[#fbbf24] px-8 pt-9 pb-12">
								<div className="pointer-events-none absolute -top-10 -right-8 h-32 w-32 rounded-full bg-white/15 blur-2xl" />
								<div className="pointer-events-none absolute -bottom-12 -left-6 h-32 w-32 rounded-full bg-white/10 blur-2xl" />
								<div className="relative flex flex-col items-center text-center">
									<div className="grid h-16 w-16 place-items-center rounded-2xl bg-white/20 backdrop-blur-sm ring-1 ring-white/40">
										<i className="hgi-stroke hgi-coins-01 text-3xl text-white"></i>
									</div>
									<h2 className="mt-4 text-xl font-semibold text-white">Confirm project cloning</h2>
									<p className="mt-1 max-w-sm text-sm font-light text-white/85">Cloning uses credits from your balance.</p>
								</div>
							</div>

							<div className="px-6 pt-5">
								<div className="flex items-start gap-3 rounded-2xl border border-amber-200 bg-amber-50 p-4">
									<i className="hgi-stroke hgi-coins-01 mt-0.5 text-lg text-amber-500"></i>
									<p className="text-sm font-light leading-relaxed text-amber-700">
										This project cloning costs <strong className="font-semibold">{data?.total_deducted_credits} credit</strong>. Would you like to continue?
									</p>
								</div>
							</div>

							<div className="flex justify-end gap-3 px-6 py-5">
								<Button
									variant="bordered"
									className="h-11 rounded-xl border-1 border-[#00000014] bg-[#c8ceda4d] px-6 text-sm font-medium text-slate-700 hover:border-slate-700"
									onPress={projectCloneModalOnOpenChange}>Cancel</Button>
								<Button
									isLoading={updateProject.isPending}
									className="h-11 rounded-xl bg-[#7659fa] px-6 text-sm font-medium text-white shadow-lg shadow-[#7659fa40] transition hover:opacity-95"
									onPress={()=>{
										updateProject.mutate(form.values)
									}}>Confirm</Button>
							</div>
						</ModalBody>
					)}
				</ModalContent>
			</Modal>
		</>
	);
}
